import { Link, useNavigate } from 'react-router-dom'
import { BuildingForm } from '@/components/buildings/BuildingForm'
import {
  useCreateBuilding,
  useSaveBuildingImagesForNewBuilding,
  type BuildingCreateInput,
  type StagedBuildingImage,
} from '@/hooks'

/**
 * Pagina de creacion de edificios con implementacion completa del formulario.
 * Usa React Hook Form + Zod para validacion y Convex mutations.
 */
export function BuildingCreate() {
  const navigate = useNavigate()
  const createMutation = useCreateBuilding()
  const saveImagesMutation = useSaveBuildingImagesForNewBuilding()

  async function handleSubmit(data: BuildingCreateInput, stagedImages: StagedBuildingImage[]) {
    try {
      const buildingId = await createMutation.mutateAsync(data)

      // Save staged images if any
      if (stagedImages.length > 0) {
        try {
          await saveImagesMutation.mutateAsync({
            buildingId,
            images: stagedImages,
          })
        } catch (imageError) {
          // Log error but don't fail the whole operation
          // Building is created, images just didn't save
          console.error('Failed to save images:', imageError)
        }
      }

      // Navigate to the buildings list after successful creation
      navigate('/buildings', {
        state: { message: '¡Edificio creado exitosamente!' },
      })
    } catch (error) {
      // Error is already captured in createMutation.error
      console.error('Failed to create building:', error)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Encabezado */}
      <div>
        <Link
          to="/buildings"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          ← Volver a Edificios
        </Link>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Crear Edificio</h1>
        <p className="mt-2 text-muted-foreground">
          Añadir un nuevo edificio o promocion al sistema.
        </p>
      </div>

      {/* Formulario */}
      <BuildingForm
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || saveImagesMutation.isPending}
        submitError={createMutation.error?.message || saveImagesMutation.error?.message}
      />
    </div>
  )
}
